export enum ValidationRuleType {
    REQUIRED = 1,
    MAX_LENGTH = 2,
    MIN_LENGTH = 3,
    REGEX = 4,
}

export interface ValidationRule {
    type: number;
    condition: string | null;
    errorMessage: string;
}

export interface FieldConfig {
    fieldType: string;
    isRequired: boolean;
    isSelection: boolean;
    fieldName: string;
    displayOrder: number;
    fieldValues: any[] | null;
    validationRules: ValidationRule[] | null;
    fieldPattern?: string | null;
}

type TranslateFn = (key: string) => string;

const formatValueWithPattern = (value: string, fieldPattern: string): string => {
    const cleanValue = value.replace(/[^a-zA-Z0-9]/g, '');
    let formattedValue = '';
    let valueIndex = 0;
    for (let i = 0; i < fieldPattern.length; i++) {
        if (valueIndex >= cleanValue.length) break;
        if (fieldPattern[i] === '#') {
            formattedValue += cleanValue[valueIndex];
            valueIndex++;
        } else {
            formattedValue += fieldPattern[i];
            if (cleanValue[valueIndex] === fieldPattern[i]) {
                valueIndex++;
            }
        }
    }
    return formattedValue;
};

const isEmptyRequiredValue = (valueToValidate: string, isSelection: boolean): boolean => {
    if (isSelection) {
        return !valueToValidate || valueToValidate === '' || valueToValidate === null || valueToValidate === undefined;
    }
    return !valueToValidate || valueToValidate.trim() === '';
};

const matchRegexRule = (rule: ValidationRule, valueToValidate: string, fieldPattern?: string | null): boolean => {
    if (!rule.condition || !valueToValidate) return true;
    const regex = new RegExp(rule.condition, 'u');
    if (regex.test(valueToValidate)) return true;
    if (fieldPattern && regex.test(valueToValidate.replace(/\s/g, ''))) return true;
    return false;
};

const applyValidationRule = (
    rule: ValidationRule,
    field: FieldConfig,
    valueToValidate: string,
    t: TranslateFn
): string => {
    switch (rule.type) {
        case ValidationRuleType.REQUIRED:
            if (isEmptyRequiredValue(valueToValidate, field.isSelection)) {
                return t(`return.${rule.errorMessage}`) || 'Bu alan zorunludur';
            }
            return '';
        case ValidationRuleType.MIN_LENGTH:
            if (rule.condition && valueToValidate.length < parseInt(rule.condition)) {
                return t(`return.${rule.errorMessage}`) || `Minimum ${rule.condition} karakter olmalıdır`;
            }
            return '';
        case ValidationRuleType.MAX_LENGTH:
            if (rule.condition && valueToValidate.length > parseInt(rule.condition)) {
                return t(`return.${rule.errorMessage}`) || `Maksimum ${rule.condition} karakter olmalıdır`;
            }
            return '';
        case ValidationRuleType.REGEX:
            if (!matchRegexRule(rule, valueToValidate, field.fieldPattern)) {
                return t(`return.${rule.errorMessage}`) || 'Geçersiz format';
            }
            return '';
        default:
            return '';
    }
};

export const getCountryIdFromIban = (
    ibanNumber: string | undefined,
    countryList: Array<{ countryId: number; countryCode?: string }>
): number | null => {
    const normalizedIban = ibanNumber?.replace(/\s/g, '').toUpperCase() ?? '';
    if (normalizedIban.length < 2 || !countryList?.length) return null;

    const ibanCountryCode = normalizedIban.substring(0, 2);
    const matchedCountry = countryList.find((c) => c.countryCode?.toUpperCase() === ibanCountryCode);
    return matchedCountry?.countryId ?? null;
};

export const validateReturnCardField = (field: FieldConfig, value: string, t: TranslateFn): string => {
    if (!field.validationRules) return '';

    let valueToValidate = value;
    if (field.fieldPattern && value) {
        valueToValidate = formatValueWithPattern(value, field.fieldPattern);
    }

    for (const rule of field.validationRules) {
        const error = applyValidationRule(rule, field, valueToValidate, t);
        if (error) return error;
    }
    return '';
};
